import './registerPage.css';
import React, {Component} from "react";
import AuthenticationService from "../services/authenticationService";
import RedirectionService from "../services/redirectionService";
import {Navigate} from "react-router-dom";
import {ToastErrorLevel} from "../../App";
import {withRouter} from "./withRouter";
import LoadingSpinner from "./loadingSpinner";
import i18nService from "../../i18n/i18nService";

interface RegisterPageState {
    token: string,
    firstName: string,
    lastName: string,
    username: string,
    password: string,
    confirmPassword: string,
    error?: string,
    isLoading: boolean,
    navigation?: string,
}

interface RegisterPageProps {
    toastGenerator: (body: string, level: ToastErrorLevel) => any,
}

class RegisterPage extends Component<RegisterPageProps, RegisterPageState> {

    constructor(props: RegisterPageProps) {
        super(props, {});
        // @ts-ignore
        const searchParams = new URLSearchParams(this.props.location?.search || '');
        const token = searchParams.get('token') || '';
        this.state = {
            token: token,
            firstName: "",
            lastName: "",
            username: "",
            password: "",
            confirmPassword: "",
            isLoading: false,
        };
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    async handleSubmit(event: any) {
        event.preventDefault();

        if (!this.state.token) {
            this.setState({error: "Invalid invitation link. Please use the link from your email."});
            return;
        }

        if (!this.state.firstName.trim() || !this.state.lastName.trim() || !this.state.username.trim()) {
            this.setState({error: "Please enter your first name, last name, and username."});
            return;
        }

        if (this.state.username.trim().length < 3) {
            this.setState({error: "Username must be at least 3 characters."});
            return;
        }

        if (!this.state.password || !this.state.confirmPassword) {
            this.setState({error: "Please enter and confirm your password."});
            return;
        }

        if (this.state.password.length < 6) {
            this.setState({error: "Password must be at least 6 characters."});
            return;
        }

        if (this.state.password !== this.state.confirmPassword) {
            this.setState({error: "Passwords do not match."});
            return;
        }

        this.setState({isLoading: true, error: undefined});

        await AuthenticationService.registerFromInvitation(
            this.state.token,
            this.state.firstName.trim(),
            this.state.lastName.trim(),
            this.state.username.trim(),
            this.state.password,
            async () => {
                this.props.toastGenerator("Registration successful! Welcome to Expense Tracker.", ToastErrorLevel.INFO);
                this.setState({
                    isLoading: false,
                    navigation: "/dashboard",
                });
            },
            async (error) => {
                this.setState({
                    error: error,
                    isLoading: false,
                });
                this.props.toastGenerator(error, ToastErrorLevel.ERROR);
            }
        );
    }

    render() {
        if (this.state.navigation) {
            return <Navigate to={this.state.navigation}/>;
        }

        return (
            <div className="RegisterPage-Wrapper">
                <div className="RegisterPage-Container">
                    <h1 className="RegisterPage-Title">{i18nService.t('register.title', 'Create Your Account')}</h1>
                    <p className="RegisterPage-Description">
                        {i18nService.t('register.description', "You've been invited to Expense Tracker. Complete the form below to finish setting up your account.")}
                    </p>

                    {this.state.isLoading && (
                        <div className="RegisterPage-Loading">
                            <LoadingSpinner size="large" message={i18nService.t('register.creatingAccount', 'Creating account...')} />
                        </div>
                    )}
                    {!this.state.isLoading && (
                    <form onSubmit={this.handleSubmit}>
                        <div className="RegisterPage-InputGroup">
                            <input
                                type="text"
                                className="RegisterPage-Input"
                                placeholder={i18nService.t('register.firstName')}
                                value={this.state.firstName}
                                onChange={(e) => this.setState({firstName: e.target.value, error: undefined})}
                                disabled={this.state.isLoading}
                                required
                            />
                            <input
                                type="text"
                                className="RegisterPage-Input"
                                placeholder={i18nService.t('register.lastName')}
                                value={this.state.lastName}
                                onChange={(e) => this.setState({lastName: e.target.value, error: undefined})}
                                disabled={this.state.isLoading}
                                required
                            />
                            <input
                                type="text"
                                className="RegisterPage-Input"
                                placeholder={i18nService.t('register.username')}
                                value={this.state.username}
                                onChange={(e) => this.setState({username: e.target.value, error: undefined})}
                                disabled={this.state.isLoading}
                                required
                                minLength={3}
                            />
                            <input
                                type="password"
                                className="RegisterPage-Input"
                                placeholder={i18nService.t('register.password')}
                                value={this.state.password}
                                onChange={(e) => this.setState({password: e.target.value, error: undefined})}
                                disabled={this.state.isLoading}
                                required
                                minLength={6}
                            />
                            <input
                                type="password"
                                className="RegisterPage-Input"
                                placeholder={i18nService.t('register.confirmPassword')}
                                value={this.state.confirmPassword}
                                onChange={(e) => this.setState({confirmPassword: e.target.value, error: undefined})}
                                disabled={this.state.isLoading}
                                required
                                minLength={6}
                            />
                        </div>

                        {this.state.error && (
                            <div className="RegisterPage-Error">{this.state.error}</div>
                        )}

                        <button
                            type="submit"
                            className="RegisterPage-Button"
                            disabled={this.state.isLoading}
                        >
                            {i18nService.t('register.createAccount')}
                        </button>
                    </form>
                    )}
                </div>
            </div>
        );
    }
}

export default withRouter(RegisterPage);


